import { Meeting } from '../services/meetingService';
import { parseDateAsUTC } from './dateUtils';

/**
 * Get hour and minute for a Date in the specified IANA timezone.
 * Using Intl avoids the double-offset bug from Date.getHours() (which always
 * returns the browser-local hour, not the target timezone's hour).
 */
export function getHourMinuteInTimezone(date: Date, timezone: string): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const map: Record<string, string> = {};
  parts.forEach(({ type, value }) => { map[type] = value; });
  const hour = map.hour === '24' ? 0 : parseInt(map.hour, 10);
  const minute = parseInt(map.minute, 10);
  return { hour, minute };
}

/**
 * Get the date string (YYYY-MM-DD) for a Date in the specified IANA timezone.
 * Used for timezone-aware "same day" comparisons.
 */
export function getDateStringInTimezone(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const map: Record<string, string> = {};
  parts.forEach(({ type, value }) => { map[type] = value; });
  return `${map.year}-${map.month}-${map.day}`;
}

// Type definitions
export interface MeetingWithLayout extends Meeting {
  column: number;
  columnCount: number;
  overlaps: number[];
}

export interface PositionedMeeting extends MeetingWithLayout {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface CollisionGroup {
  meetings: MeetingWithLayout[];
  columnCount: number;
}

// Configuration constants
export const LAYOUT_CONFIG = {
  PIXELS_PER_HOUR_WEEK: 100,
  PIXELS_PER_HOUR_DAY: 120,
  MIN_EVENT_HEIGHT: 20,
  EVENT_PADDING: 2,
};

/**
 * Detect overlapping meetings
 * Returns array with overlap information tracked
 */
export function detectOverlaps(meetings: Meeting[]): MeetingWithLayout[] {
  // Sort meetings by start time, then by duration (longer first)
  const sorted = [...meetings].sort((a, b) => {
    const aStart = typeof a.scheduled_at === 'string' ? parseDateAsUTC(a.scheduled_at).getTime() : a.scheduled_at;
    const bStart = typeof b.scheduled_at === 'string' ? parseDateAsUTC(b.scheduled_at).getTime() : b.scheduled_at;
    const aEnd = aStart + (a.duration_minutes * 60 * 1000);
    const bEnd = bStart + (b.duration_minutes * 60 * 1000);

    if (aStart !== bStart) return aStart - bStart;
    return (bEnd - bStart) - (aEnd - aStart); // Longer events first
  });

  const withOverlaps: MeetingWithLayout[] = sorted.map(meeting => ({
    ...meeting,
    column: 0,
    columnCount: 1,
    overlaps: []
  }));

  // For each meeting, find all meetings that overlap with it
  for (let i = 0; i < withOverlaps.length; i++) {
    for (let j = i + 1; j < withOverlaps.length; j++) {
      const meetingA = withOverlaps[i];
      const meetingB = withOverlaps[j];

      // Check if they overlap
      if (doMeetingsOverlap(meetingA, meetingB)) {
        withOverlaps[i].overlaps.push(meetingB.id);
        withOverlaps[j].overlaps.push(meetingA.id);
      }
    }
  }

  return withOverlaps;
}

/**
 * Check if two meetings overlap
 */
function doMeetingsOverlap(a: Meeting, b: Meeting): boolean {
  const aStart = typeof a.scheduled_at === 'string' ? parseDateAsUTC(a.scheduled_at).getTime() : a.scheduled_at;
  const bStart = typeof b.scheduled_at === 'string' ? parseDateAsUTC(b.scheduled_at).getTime() : b.scheduled_at;
  const aEnd = aStart + (a.duration_minutes * 60 * 1000);
  const bEnd = bStart + (b.duration_minutes * 60 * 1000);

  return aEnd > bStart && aStart < bEnd;
}

/**
 * Get all meetings that are connected through overlaps (transitive closure)
 */
function getCollisionGroup(
  start: MeetingWithLayout,
  allMeetings: MeetingWithLayout[]
): MeetingWithLayout[] {
  const group = new Set<number>([start.id]);
  const queue = [start];

  while (queue.length > 0) {
    const current = queue.shift()!;

    for (const overlapId of current.overlaps) {
      if (!group.has(overlapId)) {
        group.add(overlapId);
        const overlapping = allMeetings.find(m => m.id === overlapId)!;
        queue.push(overlapping);
      }
    }
  }

  return allMeetings.filter(m => group.has(m.id));
}

/**
 * Assign columns to a group of meetings using a greedy algorithm
 */
function assignColumnsToGroup(group: MeetingWithLayout[]): void {
  const columns: MeetingWithLayout[][] = [];

  for (const meeting of group) {
    let assignedColumn = -1;

    for (let col = 0; col < columns.length; col++) {
      const lastInColumn = columns[col][columns[col].length - 1];
      const meetingStart = typeof meeting.scheduled_at === 'string' ? parseDateAsUTC(meeting.scheduled_at).getTime() : meeting.scheduled_at;
      const lastEnd = typeof lastInColumn.scheduled_at === 'string'
        ? parseDateAsUTC(lastInColumn.scheduled_at).getTime() + (lastInColumn.duration_minutes * 60 * 1000)
        : lastInColumn.scheduled_at;

      // If this meeting starts after the last meeting in this column ends
      if (meetingStart >= lastEnd) {
        assignedColumn = col;
        break;
      }
    }

    // If no suitable column found, create a new one
    if (assignedColumn === -1) {
      assignedColumn = columns.length;
      columns.push([]);
    }

    meeting.column = assignedColumn;
    columns[assignedColumn].push(meeting);
  }

  // Set columnCount for all meetings in the group
  const maxColumns = columns.length;
  group.forEach(m => (m.columnCount = maxColumns));
}

/**
 * Assign columns to all meetings, grouping by collision groups
 */
export function assignColumns(meetingsWithOverlaps: MeetingWithLayout[]): CollisionGroup[] {
  const collisionGroups: CollisionGroup[] = [];
  const processed = new Set<number>();

  // Group meetings into collision groups (transitive overlap)
  for (const meeting of meetingsWithOverlaps) {
    if (processed.has(meeting.id)) continue;

    const group = getCollisionGroup(meeting, meetingsWithOverlaps);
    assignColumnsToGroup(group);

    collisionGroups.push({
      meetings: group,
      columnCount: Math.max(...group.map(m => m.column + 1))
    });

    group.forEach(m => processed.add(m.id));
  }

  return collisionGroups;
}

/**
 * Calculate pixel position and dimensions for a meeting
 */
export function calculatePosition(
  meeting: MeetingWithLayout,
  dayStartHour: number,
  pixelsPerHour: number,
  timezone: string = 'UTC'
): { top: number; height: number; left: number; width: number } {
  const meetingStartDate = typeof meeting.scheduled_at === 'string'
    ? parseDateAsUTC(meeting.scheduled_at)
    : new Date(meeting.scheduled_at);

  const { hour: meetingHour, minute: meetingMinute } = getHourMinuteInTimezone(meetingStartDate, timezone);
  const minutesFromDayStart = (meetingHour - dayStartHour) * 60 + meetingMinute;
  const top = (minutesFromDayStart / 60) * pixelsPerHour;

  // Calculate HEIGHT (based on duration in minutes)
  const durationMinutes = meeting.duration_minutes;
  const height = Math.max(
    LAYOUT_CONFIG.MIN_EVENT_HEIGHT,
    (durationMinutes / 60) * pixelsPerHour - LAYOUT_CONFIG.EVENT_PADDING
  );

  // Calculate WIDTH (percentage based on column count)
  const widthPercent = 100 / meeting.columnCount;

  // Calculate LEFT (percentage based on column index)
  const leftPercent = meeting.column * widthPercent;

  return {
    top: Math.round(top),
    height: Math.round(height),
    left: leftPercent,
    width: widthPercent
  };
}

/**
 * Get all meetings for a specific day with calculated layout positions
 */
export function getMeetingsForDayWithLayout(
  date: Date,
  allMeetings: Meeting[],
  dayStartHour: number = 8,
  pixelsPerHour: number = LAYOUT_CONFIG.PIXELS_PER_HOUR_WEEK,
  timezone: string = 'UTC'
): PositionedMeeting[] {
  // Compare dates in the target timezone so day boundaries are correct
  const targetDateStr = getDateStringInTimezone(date, timezone);

  const dayMeetings = allMeetings.filter(meeting => {
    const meetingDate = typeof meeting.scheduled_at === 'string'
      ? parseDateAsUTC(meeting.scheduled_at)
      : new Date(meeting.scheduled_at);
    return getDateStringInTimezone(meetingDate, timezone) === targetDateStr;
  });

  // If no meetings, return empty array
  if (dayMeetings.length === 0) {
    return [];
  }

  // Apply full layout algorithm
  const withOverlaps = detectOverlaps(dayMeetings);
  const groups = assignColumns(withOverlaps);

  // Calculate positions
  const positioned: PositionedMeeting[] = [];

  for (const group of groups) {
    for (const meeting of group.meetings) {
      const position = calculatePosition(meeting, dayStartHour, pixelsPerHour, timezone);
      positioned.push({
        ...meeting,
        ...position
      });
    }
  }

  return positioned;
}
